import { useEffect, useRef, useState } from 'react'
import { Button, Stack, Typography } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import { useTranslation } from 'react-i18next'
import FsContainer from '../../common/Container'
import { useAppDispatch, useAppSelector } from '../../../../store/hooks'
import {
  getFileCharacteristics,
  getOriginalValues,
  getVariables,
  goBackETLStep,
  setEtlFiles,
  updateEtlStep,
  updateVariable,
} from '../../../../store/actions'
import FsAlert, { FsAlertRef } from '../../../common/atoms/Alert'
import FsVariableGroup from './FsVaraibleGroup'
import FsNewVariableGroup from './FsNewVariableGroup'
import { useLoading } from '../../../../shared/hooks'
import ACTIONS from '../../../../store/constants'

const FsValueMapping = ({ setIsLoading }: { setIsLoading: (isLoading: boolean) => void }) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const alertRef = useRef<FsAlertRef>(null)
  const { etlFiles, latestEtlStatus, etlFileCharacteristics, etlValueMappings, etlOriginalValues } =
    useAppSelector((state) => state.currentEtl)

  const [open, setOpen] = useState<boolean>(false)
  const [isCompleted, setIsCompleted] = useState<boolean>(false)

  useEffect(() => {
    etlValueMappings?.forEach((char) => {
      if (!etlOriginalValues || !etlOriginalValues[char.id]) {
        dispatch(getOriginalValues({ characteristicId: char.id }))
      }
    })
  }, [etlValueMappings])

  useEffect(() => {
    if (latestEtlStatus) {
      dispatch(setEtlFiles({ runId: latestEtlStatus.id }))
    }
  }, [latestEtlStatus])

  useEffect(() => {
    if (!etlFileCharacteristics && latestEtlStatus) {
      dispatch(getFileCharacteristics())
    }
    if (!etlValueMappings && etlFileCharacteristics) {
      dispatch(getVariables())
    }
  }, [etlFileCharacteristics, etlFiles, etlValueMappings, latestEtlStatus])

  const { isFetching } = useLoading([
    ACTIONS.ETL.SET_ETL_FILES,
    ACTIONS.ETL.GET_FILE_CHARACTERISTICS,
    ACTIONS.ETL.GET_VARIABLES,
    ACTIONS.ETL.SET_ETL_STATUS,
    ACTIONS.ETL.ADD_VARIABLE,
    ACTIONS.ETL.DELETE_VARIABLE,
    ACTIONS.ETL.DELETE_CHAR_VARIABLE,
  ])

  useEffect(() => {
    setIsLoading(isFetching)
  }, [isFetching])

  useEffect(() => {
    setIsCompleted(
      !etlValueMappings?.find((charMap) => {
        const allValues = etlOriginalValues ? etlOriginalValues[charMap.id] : []
        const selectedValues = charMap.mappings.flatMap((m) => m.originalValues ?? []).length
        return (
          allValues &&
          charMap &&
          charMap.mappings &&
          (charMap.mappings.find((map) => {
            return !map.label || !map.originalValues || map.originalValues.length === 0
          }) ||
            selectedValues !== allValues.length)
        )
      }),
    )
  }, [etlValueMappings, etlOriginalValues])

  const handleSave = async (shouldGoNext: boolean) => {
    try {
      if (etlValueMappings) {
        setIsLoading(true)
        if (!shouldGoNext) {
          await Promise.all(
            etlValueMappings.flatMap((map) =>
              map.mappings.map(async (m) => await dispatch(updateVariable({ variable: m }))),
            ),
          )
          alertRef.current?.openAlert({
            severity: 'success',
            message: t('value.mappings.saved'),
          })
        } else if (shouldGoNext) {
          if (isCompleted) {
            await Promise.all(
              etlValueMappings.flatMap((map) =>
                map.mappings.map(async (m) => await dispatch(updateVariable({ variable: m }))),
              ),
            )
            await dispatch(updateEtlStep({ step: 'ValueMapped' }))
          } else {
            alertRef.current?.openAlert({
              severity: 'error',
              message: t('something.went.wrong'),
            })
          }
        }
      }
    } catch {
      alertRef.current?.openAlert({
        severity: 'error',
        message: t('something.went.wrong'),
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <FsContainer
      footer={
        <Stack width="100%" direction="row" justifyContent="space-between">
          <Stack>
            <Button onClick={() => dispatch(goBackETLStep())}>{t('back')}</Button>
          </Stack>
          <Stack direction="row" gap={1}>
            <Button variant="outlined" onClick={() => handleSave(false)}>
              {t('save')}
            </Button>
            <Button variant="contained" onClick={() => handleSave(true)} disabled={!isCompleted}>
              {t('save.continue')}
            </Button>
          </Stack>
        </Stack>
      }
    >
      <Stack gap={2}>
        <Typography>{t('value.mapping.description')}</Typography>
        {etlValueMappings?.map((mapping, index) => (
          <FsVariableGroup mapping={mapping} key={`mapping_char_${index}`} />
        ))}
        {open ? <FsNewVariableGroup setOpen={setOpen} /> : null}
        <Button startIcon={<AddIcon />} variant="outlined" fullWidth onClick={() => setOpen(true)}>
          {t('mapping.add')}
        </Button>
      </Stack>
      <FsAlert ref={alertRef} />
    </FsContainer>
  )
}

export default FsValueMapping
